import type {AgeniumQuestionTranscript} from '@/APIs/Agenium/api';
import type {ReactNode} from 'react';
import {v5} from 'uuid';

const UUID_NAMESPACE = '3baf80cf-7094-4eed-8f53-3037c4ffc029';

const EXCLUDED_QUESTIONS = [
    "Agent's Contract Number",
    'What plan is being applied for?',
    'Bootstrapped Data for Med Auth Signature',
    'Amplify: What is the original quote amount?',
    'Is the Payor the same as the Proposed Insured',
    'Is the Owner the same as the Proposed Insured',
    'Primary Beneficiary Details',
    'Would you like to add a contingent beneficiary?',
];

export type AgeniumReviewQuestion = {
    id: string;
    section: string;
    question: string;
    answer?: string;
    answer2?: string;
    helperText?: string | ReactNode;
    lastModifiedDate?: string;
};

const shouldExcludeQuestion = (question: string) =>
    EXCLUDED_QUESTIONS.some((excludedQuestion) => question.includes(`>${excludedQuestion}<`));

export const buildAgeniumReviewQuestions = (transcript: AgeniumQuestionTranscript[]): AgeniumReviewQuestion[] => {
    const transcriptWithIds: AgeniumReviewQuestion[] = transcript.map((item) => ({
        id: v5(item.question, UUID_NAMESPACE),
        ...item,
    }));

    const uniqueItems = new Map();
    const prioritizedQuestions: (AgeniumReviewQuestion | undefined)[] = [undefined, undefined, undefined];
    const otherQuestions =
        transcriptWithIds.reduce<Array<AgeniumReviewQuestion>>((acc, item) => {
            const nextItem = {...item};
            if (
                !nextItem.question.includes('%') &&
                nextItem.answer != null &&
                nextItem.answer !== '' &&
                !shouldExcludeQuestion(nextItem.question) &&
                !uniqueItems.has(nextItem.id)
            ) {
                uniqueItems.set(nextItem.id, true);
                if (nextItem.question.includes('Death Benefit Option')) {
                    nextItem.question = 'Death Benefit Option';
                    if (nextItem.section.includes('Section C:')) {
                        prioritizedQuestions[1] = nextItem;
                    }
                    return acc;
                } else if (nextItem.question.includes('Death Benefit Qualification Test')) {
                    nextItem.question = 'Life Insurance Qualification Test';
                    prioritizedQuestions[2] = nextItem;
                    return acc;
                } else if (
                    nextItem.question.includes(
                        'I elect to have my premium allocated directly to the accounts as indicated below.',
                    )
                ) {
                    const allocationLabel = nextItem.question.split('<p><br></p><p>Percent of ')[1]?.split('</p>')[0];
                    if (!allocationLabel) {
                        acc.push(nextItem);
                        return acc;
                    }
                    if (prioritizedQuestions[0] != null) {
                        prioritizedQuestions[0].answer2 = `${allocationLabel}: ${nextItem.answer}%`;
                        return acc;
                    }
                    nextItem.answer = `${allocationLabel}: ${nextItem.answer}%`;
                    nextItem.question = 'Index Allocation';
                    prioritizedQuestions[0] = nextItem;
                    return acc;
                } else if (nextItem.question.includes('What City will the owner sign the application in?')) {
                    nextItem.question = 'Identify the city the owner will sign the application.';
                }

                acc.push(nextItem);
            }
            return acc;
        }, []) || [];

    return [...(prioritizedQuestions.filter((value) => value != null) as AgeniumReviewQuestion[]), ...otherQuestions];
};
